import React, { Component } from "react";
import { CustomiseContext } from "../CustomiseContext";
import { Forms, Screens } from "src/scenes/Customise";
import CustomAppIcon from "../components/CustomAppIcon";
import CloseIcon from "src/img/remove-hero-image.png";
import UploadIconImg from "src/img/UploadIcon.png";
import {
    CustomiseStepActions,
    CustomiseStepButton,
    CustomiseStepCaption,
    CustomiseStepCard,
    CustomiseStepCardTitle,
    CustomiseStepContent,
    CustomiseStepDivider,
    CustomiseStepHeader,
    CustomiseStepShell,
    CustomiseStepTitle,
    Toast,
    ImageCropModal,
    PortalCaption,
    PortalMetricLabel,
} from "src/components";
import {
    IconsCard,
    IconSlot,
    AddIconButton,
    ButtonsContainer,
    PhoneContainer,
    IconGroupsGrid,
    IconGroupCard
} from "./Style";
import { ICON_STATUS } from "./Finish";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

class UploadIcon extends Component {
    static contextType = CustomiseContext;

    state = {
        showCropperModal: false,
        imageToCrop: null
    };

    draggedIndex = null; 

    onDragStart = (e, index) => {
        this.draggedIndex = index;
    };

    onDropIcon = (e, dropIndex) => {
        const { iconFile, icons } = this.context;

        const merged = [
            ...iconFile.map(icon => ({ ...icon, source: "file", status: icon.status || ICON_STATUS.PENDING })),
            ...icons.map(icon => ({ ...icon, source: "backend", status: icon.status || ICON_STATUS.PENDING, preview: icon.preview || icon.iconUrl })),
        ];

        if (this.draggedIndex === null || this.draggedIndex >= merged.length) return;

        const [moved] = merged.splice(this.draggedIndex, 1);
        merged.splice(dropIndex, 0, moved);

        const updated = merged.map((icon, idx) => ({ ...icon, selected: idx === 0 }));

        this.context.uploadFile("iconFile", updated.filter(i => i.source === "file"));
        this.context.uploadFile("icons", updated.filter(i => i.source === "backend"));
        this.draggedIndex = null;
    };

    onDrop = (droppedFiles) => {
        const fileTypeErrorMessage = "Your icons must be PNG or JPEG and less than 5MB each.";
        if (!droppedFiles || droppedFiles.length === 0) {
            Toast.error(fileTypeErrorMessage);
            return;
        }

        const validFiles = droppedFiles.filter(file =>
            ["image/jpeg", "image/png"].includes(file.type) && file.size <= 5242880
        );

        if (validFiles.length === 0) {
            Toast.error(fileTypeErrorMessage);
            return;
        }

        this.setState({
            imageToCrop: validFiles[0],
            showCropperModal: true
        });
    };

    removeIcon = (icon) => {
        const { iconFile, icons } = this.context;
        if (!icon) return;

        if (icon.status === ICON_STATUS.ACTIVE) {
            const updatedIcons = icons.map(i =>
                i.preview === icon.preview ? { ...i, status: ICON_STATUS.REMOVE, selected: false } : i
            );
            this.context.uploadFile("icons", updatedIcons);
        } else {
            if (icon.source === "file") {
                const updatedFiles = iconFile.filter(f => f.preview !== icon.preview);
                this.context.uploadFile("iconFile", updatedFiles);
            } else {
                const updatedIcons = icons.filter(i => i.preview !== icon.preview);
                this.context.uploadFile("icons", updatedIcons);
            }
        }
    };

    undoRemoveIcon = (icon) => {
        const { iconFile, icons } = this.context;

        if (!icon) return;

        if (icon.source === "file") {
            const updatedFiles = iconFile.map(f =>
                f.preview === icon.preview ? { ...f, status: ICON_STATUS.PENDING } : f
            );
            this.context.uploadFile("iconFile", updatedFiles);
        } else {
            const updatedIcons = icons.map(i =>
                i.preview === icon.preview ? { ...i, status: ICON_STATUS.ACTIVE } : i
            );
            this.context.uploadFile("icons", updatedIcons);
        }
    };

    handleCropComplete = (croppedFile) => {
        if (!croppedFile) return;
        const previewUrl = URL.createObjectURL(croppedFile);

        // Upload on final save so discarded app icons do not appear in storage management.
        const updatedIcons = [
            { preview: previewUrl, fileToUpload: croppedFile, status: ICON_STATUS.PENDING },
            ...this.context.iconFile
        ].slice(0, 12).map((icon, idx) => ({ ...icon, selected: idx === 0 }));

        this.context.uploadFile("iconFile", updatedIcons);
        this.setState({ showCropperModal: false, imageToCrop: null });
    };

    render() {
        const { showCropperModal, imageToCrop } = this.state;
        const { iconFile, icons, companyName, currentScreen, currentForm, screenIds, leftScreen, rightScreen, colourPalette, assetLogoUrl, currentLogoUrl, assetIconUrl } = this.context;

        const allIcons = [
            ...iconFile.map(icon => ({ ...icon, source: "file", status: icon.status || ICON_STATUS.PENDING, preview: icon.preview || icon.iconUrl || "" })),
            ...icons.map(icon => ({ ...icon, source: "backend", status: icon.status || ICON_STATUS.PENDING, preview: icon.preview || icon.iconUrl || "" }))
        ].slice(0, 12);

        // Active icons
        const activeIcons = allIcons.filter(icon => icon.status === ICON_STATUS.ACTIVE);

        // Pending Approval = only pending
        const pendingAdditionIcons = allIcons.filter(icon => icon.status === ICON_STATUS.PENDING);

        // Pending Removal = only remove
        const pendingRemovalIcons = allIcons.filter(icon => icon.status === ICON_STATUS.REMOVE);

        while (activeIcons.length < 6) activeIcons.push(null);
        while (pendingAdditionIcons.length < 6) pendingAdditionIcons.push(null);
        while (pendingRemovalIcons.length < 6) pendingRemovalIcons.push(null);

        const selectedIcon = allIcons.find(icon => icon.selected) || allIcons[0];
        const selectedIconUrl = selectedIcon?.preview || assetIconUrl || "";
        const hasExistingIcons = allIcons.some(icon => icon && (icon.preview || icon.iconUrl));
        const showDefault = !hasExistingIcons;
        
        return (
            <>
                <CustomiseStepShell $compact>
                    <CustomiseStepContent $compact>
                        <CustomiseStepHeader>
                            <div>
                                <CustomiseStepTitle>Step 3: Custom App Icon (Optional)</CustomiseStepTitle>
                                <CustomiseStepCaption margin="0.25rem 0 0">
                                    Upload a custom app icon to strengthen your brand identity in the Me Business app.
                                </CustomiseStepCaption>
                            </div>
                        </CustomiseStepHeader>

                        <CustomiseStepCard>
                            <CustomiseStepCardTitle>Upload Your App Icon</CustomiseStepCardTitle>
                            <PortalCaption margin="0">
                                Image Format: JPEG, PNG File - 1024 x 1024 px
                            </PortalCaption>
                            <IconGroupsGrid>
                                {showDefault && (
                                    <img src={UploadIconImg} alt="UploadBackground" width="120px" style={{ marginBottom: "10px" }}/>
                                )}
                                {activeIcons.some(icon => icon) && (
                                    <IconGroupCard>
                                        <PortalMetricLabel>Active Icons</PortalMetricLabel>
                                        <IconsCard>
                                            {activeIcons.map((icon, idx) => (
                                                <IconSlot hasIcon={!!icon} key={idx}>
                                                    {icon && (
                                                        <>
                                                            <img src={icon.preview} alt={`Icon ${idx + 1}`} />
                                                            <div className="remove-btn" onClick={() => this.removeIcon(icon)}>
                                                                <img src={CloseIcon} alt="remove" width="5px" height="5px" />
                                                            </div>
                                                        </>
                                                    )}
                                                </IconSlot>
                                            ))}
                                        </IconsCard>
                                    </IconGroupCard>
                                )}
                                {pendingAdditionIcons.some(icon => icon) && (
                                    <IconGroupCard>
                                        <PortalMetricLabel>Pending Approval</PortalMetricLabel>
                                        <IconsCard>
                                            {pendingAdditionIcons.map((icon, idx) => (
                                                <IconSlot
                                                    key={idx}
                                                    draggable={!!icon}
                                                    hasIcon={!!icon}
                                                    onDragStart={(e) => icon && this.onDragStart(e, idx)}
                                                    onDragOver={(e) => e.preventDefault()}
                                                    onDrop={(e) => this.onDropIcon(e, idx)}
                                                >
                                                    {icon && (
                                                        <>
                                                            <img src={icon.preview} alt={`Icon ${idx + 1}`} />
                                                            <div className="remove-btn" onClick={() => this.removeIcon(icon)}>
                                                                <img src={CloseIcon} alt="remove" width="5px" height="5px" />
                                                            </div>
                                                        </>
                                                    )}
                                                </IconSlot>
                                            ))}
                                        </IconsCard>
                                    </IconGroupCard>
                                )}
                                {pendingRemovalIcons.some(icon => icon) && (
                                    <IconGroupCard>
                                        <PortalMetricLabel>Pending Removal</PortalMetricLabel>
                                        <IconsCard>
                                            {pendingRemovalIcons.map((icon, idx) => (
                                                <IconSlot hasIcon={!!icon} key={idx}>
                                                    {icon && (
                                                        <>
                                                            <img src={icon.preview} alt={`Icon ${idx + 1}`} />
                                                            <div className="remove-btn" onClick={() => this.undoRemoveIcon(icon)}>
                                                                <span style={{ marginTop: "-4px", marginLeft: "1px" }}>↺</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </IconSlot>
                                            ))}
                                        </IconsCard>
                                    </IconGroupCard>
                                )}
                            </IconGroupsGrid>

                            <AddIconButton onChange={(files) => this.onDrop(files)} width="160px" height="45px">
                                Add App Icon
                            </AddIconButton>
                        </CustomiseStepCard>

                        <CustomiseStepCard>
                            <PortalCaption margin="0">
                                *Your app icon will become available after we submit it to the app store. It will only be shown to users in your company.
                            </PortalCaption>
                        </CustomiseStepCard>

                        <CustomiseStepDivider />

                        <PortalCaption margin="0">
                            Click <b>Next</b> to proceed to <b>Step 4:</b> Select Your App&rsquo;s Starting Screen.
                        </PortalCaption>

                        <CustomiseStepActions>
                            <ButtonsContainer>
                                <CustomiseStepButton onClick={() => this.context.previousClick(Forms.COLOR, Screens.MAIN)}>
                                    Back
                                </CustomiseStepButton>

                                <CustomiseStepButton
                                    $variant="primary"
                                    onClick={this.context.nextClick}
                                >
                                    Next
                                </CustomiseStepButton>
                            </ButtonsContainer>
                        </CustomiseStepActions>
                    </CustomiseStepContent>

                    <PhoneContainer>
                        <CustomAppIcon
                            companyName={companyName}
                            logoTopOffset={45}
                            phoneFrameTopOffset={30}
                            currentScreen={currentScreen}
                            currentForm={currentForm}
                            screenIds={screenIds}
                            leftScreen={leftScreen}
                            rightScreen={rightScreen}
                            colourPalette={colourPalette}
                            assetLogoUrl={currentLogoUrl || assetLogoUrl}
                            assetIconUrl={selectedIconUrl}
                        />
                    </PhoneContainer>
                    <ImageCropModal
                        imageSrc={imageToCrop ? URL.createObjectURL(imageToCrop) : null}
                        isOpen={showCropperModal}
                        onClose={() => this.setState({ showCropperModal: false, imageToCrop: null })}
                        onCropComplete={this.handleCropComplete}
                        aspectRatios={[{ width: 1, height: 1, label: "1:1", standardName: ASPECT_RATIO_LABEL.SQUARE }]}
                        allowedOrientations={["landscape"]}
                        lockedAspectRatio={undefined}
                    />
                </CustomiseStepShell>
            </>
        );
    }
}

export default UploadIcon;
